// src/features/imprint/utils/calendarPricing.js

/**
 * Check if a tier's sale price is currently active.
 * Compares current timestamp directly against ISO date strings,
 * preserving timezone offset (e.g. "-05:00") from the source data.
 *
 * @param {Object} tier - Tier price object
 * @returns {boolean}
 */
export function isSalePriceActive(tier) {
    if (
        !tier ||
        tier.sale_price === undefined ||
        !tier.sale_price_effectivedate ||
        !tier.sale_price_terminationdate
    ) {
        return false;
    }

    const now = new Date();
    const effectiveDate = new Date(tier.sale_price_effectivedate);
    const terminationDate = new Date(tier.sale_price_terminationdate);

    const active = now >= effectiveDate && now <= terminationDate;

    console.log('[calendarPricing] isSalePriceActive:', {
        now: now.toISOString(),
        effectiveDate: effectiveDate.toISOString(),
        terminationDate: terminationDate.toISOString(),
        active
    });

    return active;
}

/**
 * Calculate unit price for calendar products based on total quantity.
 * Uses tier price matrix from backend; honours sale_price when dates are active.
 *
 * Logic from old architecture:
 * - Sum all calendar quantities
 * - Find matching tier price based on total
 * - If the matched tier has an active sale_price — use it instead of regular price
 * - Apply that price to ALL calendars
 *
 * @param {number} totalQuantity - Total quantity of all calendars combined
 * @param {Array} tierPrices - Tier price array from backend (prices.tier_price)
 * @returns {number} - Price per unit for current total quantity
 */
export function calculateCalendarPriceByQty(totalQuantity, tierPrices, b2bTiers = null) {
    if (!tierPrices || tierPrices.length === 0) {
        console.warn('[calendarPricing] No tier prices available');
        return 0;
    }

    // Edge case: quantity less than 1
    if (totalQuantity < 1) {
        console.log('[calendarPricing] Quantity < 1, returning 0');
        return 0;
    }

    // Start with first tier as default
    let matchedTier = tierPrices[0];

    console.log('[calendarPricing] Calculating price for total qty:', totalQuantity);
    console.log('[calendarPricing] Available tiers:', tierPrices);

    // Find the matching tier (highest QtyMin that total still meets)
    tierPrices.forEach(tier => {
        // Skip MSRP tier (not a real quantity tier)
        if (tier.QtyMin === 'MSRP') return;

        const qtyMin = parseFloat(tier.QtyMin || 0);

        if (totalQuantity >= qtyMin) {
            matchedTier = tier;
            console.log('[calendarPricing] Matched tier:', { qtyMin, price: tier.price });
        }
    });

    // Resolve effective price: prefer sale_price when active
    let priceByQty = parseFloat(matchedTier.price);

    // B2B override — sort ascending so last-match gives highest applicable tier
    if (b2bTiers) {
        const sortedB2b = [...b2bTiers].sort((a, b) => parseFloat(a.QtyMin || 0) - parseFloat(b.QtyMin || 0));
        let b2bEntry = null;
        sortedB2b.forEach(t => {
            if (totalQuantity >= parseFloat(t.QtyMin || 0)) b2bEntry = t;
        });
        if (b2bEntry) {
            const b2bPrice = parseFloat(b2bEntry.price);
            if (!isNaN(b2bPrice)) {
                console.log('[calendarPricing] ✅ B2B price active. Using b2b price:', b2bPrice, '(was:', priceByQty, ')');
                return b2bPrice;
            }
        }
    }

    if (isSalePriceActive(matchedTier)) {
        const salePrice = parseFloat(matchedTier.sale_price);
        if (!isNaN(salePrice)) {
            console.log('[calendarPricing] ✅ Sale price active. Using sale_price:', salePrice, '(was:', priceByQty, ')');
            priceByQty = salePrice;
        }
    }

    console.log('[calendarPricing] Final price per unit:', priceByQty);
    return priceByQty;
}

/**
 * Calculate total quantity across all calendar products
 * 
 * @param {Array} calendarProducts - Array of calendar objects with qty field
 * @returns {number} - Sum of all quantities
 */
export function calculateTotalCalendarQty(calendarProducts) {
    if (!calendarProducts || calendarProducts.length === 0) {
        return 0;
    }
    
    const total = calendarProducts.reduce((sum, product) => {
        return sum + parseInt(product.qty || 0, 10);
    }, 0);
    
    console.log('[calendarPricing] Total calendar qty:', total);
    return total;
}

/**
 * Get tier price matrix for calendar products
 * Tries to extract from various locations in product JSON
 * 
 * @param {Object} fullProductJson - Full product JSON from backend
 * @param {string} sku - Optional SKU to look up specific configurable
 * @returns {Array} - Tier price array or empty array if not found
 */
export function getCalendarTierPrices(fullProductJson, sku = null) {
    if (!fullProductJson) {
        console.warn('[calendarPricing] No full product JSON');
        return [];
    }
    
    // Try multiple locations for tier prices
    let tierPrices = [];
    
    // Location 1: Direct prices.tier_price
    if (fullProductJson.prices?.tier_price) {
        tierPrices = fullProductJson.prices.tier_price;
        console.log('[calendarPricing] Found tier prices in prices.tier_price');
    }
    
    // Location 2: prices_2.tier_price (alternative location)
    if (tierPrices.length === 0 && fullProductJson.prices_2?.tier_price) {
        tierPrices = fullProductJson.prices_2.tier_price;
        console.log('[calendarPricing] Found tier prices in prices_2.tier_price');
    }
    
    // Location 3: Configurable array with SKU
    if (tierPrices.length === 0 && sku && fullProductJson.configurable_array?.[sku]) {
        const configData = fullProductJson.configurable_array[sku];
        
        if (configData.prices?.tier_price) {
            tierPrices = configData.prices.tier_price;
            console.log('[calendarPricing] Found tier prices in configurable_array');
        } else if (configData.prices_2?.tier_price) {
            tierPrices = configData.prices_2.tier_price;
            console.log('[calendarPricing] Found tier prices in configurable_array.prices_2');
        }
    }
    
    // Validate tier prices array
    if (!Array.isArray(tierPrices) || tierPrices.length === 0) {
        console.warn('[calendarPricing] No valid tier prices found');
        return [];
    }
    
    console.log('[calendarPricing] Loaded tier prices:', tierPrices);
    return tierPrices;
}
